import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getAssessmentQuestions, createAssessment, getAssessments, getAssessment, getOrganizations, checkAssessmentEligibility, getPolicyTemplates, adoptPolicyTemplate, getOrganizationPoliciesV2 } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useAdminMode } from '../context/AdminModeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import PortfolioAssessments from '../components/PortfolioAssessments';
import { 
  ClipboardCheck, 
  ChevronRight, 
  ChevronLeft,
  CheckCircle,
  Shield,
  AlertTriangle,
  TrendingUp,
  Check,
  X,
  Download,
  FileText,
  Upload,
  Building2,
  AlertCircle,
  Loader2,
  Calendar,
  Clock,
  Award,
  RefreshCw,
  ExternalLink,
  Eye
} from 'lucide-react';

// Question to Policy Category/Keyword mapping for matching PH Library templates
// Maps assessment question IDs to specific policy titles in the PH Library
const QUESTION_POLICY_MAPPING = {
  // q0 is just asking who referred them - no policy needed
  q1: ['Adult Participants, Minor Athletes, and Exceptions Policy', 'Adult Participants, Minor Members, and Exceptions Policy'],
  q2: ['Cardiac Emergency Response Plan'],
  q3: ['Severe Weather Policy'],
  q4: ['Concussion Planning Policy'],
  q5: ['Enforcement Policy', 'Background Check Appeals Policy'],
  q6: ['Prohibited Conduct Policy', 'Athlete Safety Policy'],
  q7: ['Education and Training Policy'],
  q8: ['Prohibited Conduct Policy'],
  q9: ['Athlete Safety Policy'], // Policy review/communication
  q10: ['Background Check Policy'],
  q11: ['One-on-One Interactions Policy'],
  q12: ['Heat Illness Policy'],
  q13: ['Facility Safety and Security Plan'],
  q14: ['Mandatory Reporting Policy', 'Mandatory Reporting Website Template'],
  q15: [], // Lifeguard requirement - no specific policy template
  q16: [], // Lifesaving equipment - covered by facility safety
};

export default function Assessments() {
  const [assessments, setAssessments] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssessment, setShowAssessment] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedOrgType, setSelectedOrgType] = useState('sports');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [orgLogo, setOrgLogo] = useState(null);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [downloadingPolicy, setDownloadingPolicy] = useState(null);
  // PH Library policies state
  const [libraryPolicies, setLibraryPolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  // Adopted policies state
  const [adoptedPolicies, setAdoptedPolicies] = useState([]);
  const [adoptingPolicy, setAdoptingPolicy] = useState(null);
  // Eligibility and renewal workflow state
  const [eligibility, setEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const { user, isSuperAdmin } = useAuth();
  const { isPlatformMode, isOrganizationMode, selectedOrg } = useAdminMode();
  const { id: assessmentId } = useParams();
  const navigate = useNavigate();

  // Sync selectedOrgId with AdminModeContext's selectedOrg
  useEffect(() => {
    if (isOrganizationMode && selectedOrg) {
      setSelectedOrgId(selectedOrg.id);
      setOrgName(selectedOrg.name);
      setSelectedOrgType(selectedOrg.org_type || 'sports');
    }
  }, [isOrganizationMode, selectedOrg]);

  // Check assessment eligibility when org is selected
  const checkEligibility = useCallback(async (orgId) => {
    if (!orgId) return;
    setCheckingEligibility(true);
    try {
      const response = await checkAssessmentEligibility(orgId);
      setEligibility(response.data);
    } catch (error) {
      console.error('Failed to check eligibility:', error);
      setEligibility(null);
    } finally {
      setCheckingEligibility(false);
    }
  }, []);

  // Check eligibility when selectedOrgId changes
  useEffect(() => {
    if (selectedOrgId) {
      checkEligibility(selectedOrgId);
    }
  }, [selectedOrgId, checkEligibility]);

  // Fetch policy templates from PH Library and adopted policies when results are shown
  useEffect(() => {
    const fetchPoliciesData = async () => {
      if (!result) return;
      
      setLoadingPolicies(true);
      try {
        // Fetch policies filtered by org type
        const orgTypeFilter = selectedOrgType === 'fitness' ? 'fitness' : 'youth_sports';
        const [templatesRes, adoptedRes] = await Promise.all([
          getPolicyTemplates({ org_type: orgTypeFilter }),
          selectedOrgId ? getOrganizationPoliciesV2({ organization_id: selectedOrgId }) : Promise.resolve({ data: [] })
        ]);
        
        setLibraryPolicies(templatesRes.data || []);
        // Get the source template IDs of adopted policies
        const adopted = (adoptedRes.data || [])
          .filter(p => p.adoption_status === 'adopted')
          .map(p => p.source_template_id);
        setAdoptedPolicies(adopted);
      } catch (error) {
        console.error('Failed to fetch policies:', error);
        // Try fetching just templates as fallback
        try {
          const response = await getPolicyTemplates();
          setLibraryPolicies(response.data || []);
        } catch (fallbackError) {
          console.error('Failed to fetch all policies:', fallbackError);
        }
      } finally {
        setLoadingPolicies(false);
      }
    };

    fetchPoliciesData();
  }, [result, selectedOrgType, selectedOrgId]);

  // Adopt a policy template
  const handleAdoptPolicy = async (policy) => {
    if (!selectedOrgId) {
      toast.error('Please select an organization first');
      return;
    }
    
    setAdoptingPolicy(policy.id);
    try {
      await adoptPolicyTemplate({ 
        template_id: policy.id 
      });
      
      toast.success(`Policy "${policy.title}" adopted successfully!`);
      
      // Update adopted policies list
      setAdoptedPolicies(prev => [...prev, policy.id]);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to adopt policy';
      toast.error(message);
    } finally {
      setAdoptingPolicy(null);
    }
  };

  // Check if a policy is adopted
  const isPolicyAdopted = (policyId) => {
    return adoptedPolicies.includes(policyId);
  };

  // Fetch a specific assessment when viewing details
  const fetchAssessmentById = useCallback(async (id) => {
    try {
      setLoading(true);
      const [assessmentRes, orgsRes] = await Promise.all([
        getAssessment(id),
        getOrganizations(),
      ]);
      setResult(assessmentRes.data);
      setOrganizations(orgsRes.data);
      // Find the org name and type for this assessment
      const org = orgsRes.data.find(o => o.id === assessmentRes.data.organization_id);
      if (org) {
        setOrgName(org.name);
        setSelectedOrgType(org.org_type || 'sports');
        setSelectedOrgId(org.id); // Set org ID for policy adoption
        // Store in sessionStorage so the API header interceptor can use it
        sessionStorage.setItem('selectedOrg', JSON.stringify({ id: org.id, name: org.name }));
      }
    } catch (error) {
      toast.error('Failed to fetch assessment details');
      navigate('/assessments');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      // Use selectedOrg from AdminModeContext for filtering
      if (isOrganizationMode && selectedOrg) {
        params.organization_id = selectedOrg.id;
      }
      const [assessmentsRes, orgsRes] = await Promise.all([
        getAssessments(params),
        getOrganizations(),
      ]);
      setAssessments(assessmentsRes.data);
      setOrganizations(orgsRes.data);
      
      // If in organization mode, use the selected org; otherwise use first org
      if (isOrganizationMode && selectedOrg) {
        setSelectedOrgId(selectedOrg.id);
        setOrgName(selectedOrg.name);
        setSelectedOrgType(selectedOrg.org_type || 'sports');
      } else if (orgsRes.data.length > 0) {
        setSelectedOrgId(orgsRes.data[0].id);
        setOrgName(orgsRes.data[0].name);
        setSelectedOrgType(orgsRes.data[0].org_type || 'sports');
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [isOrganizationMode, selectedOrg]);

  useEffect(() => {
    if (assessmentId) {
      // Viewing a specific assessment
      fetchAssessmentById(assessmentId);
    } else if (isSuperAdmin() && isPlatformMode) {
      // Platform mode - will show portfolio view
      setLoading(false);
    } else {
      // Listing assessments
      fetchData();
    }
  }, [assessmentId, fetchAssessmentById, fetchData, isSuperAdmin, isPlatformMode]);

  // Super Admin Platform Mode - Show Portfolio View (only when not viewing a specific assessment)
  if (!assessmentId && isSuperAdmin() && isPlatformMode) {
    return (
      <div data-testid="assessments-page" className="space-y-6 animate-fade-in">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
              Safety Assessments
            </h1>
            <p className="text-muted-foreground mt-1">
              Portfolio-level assessment analytics across all organizations
            </p>
          </div>
        </div>
        <PortfolioAssessments />
      </div>
    );
  }

  // Super Admin Organization Mode - No org selected (only when not viewing a specific assessment)
  if (!assessmentId && isSuperAdmin() && isOrganizationMode && !selectedOrg) {
    return (
      <div data-testid="assessments-page" className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
            Safety Assessments
          </h1>
          <p className="text-muted-foreground mt-1">Select an organization to manage assessments</p>
        </div>
        
        <Alert className="bg-yellow-500/10 border-yellow-500/30">
          <AlertCircle className="w-4 h-4 text-yellow-600" />
          <AlertDescription className="text-yellow-600">
            Please select an organization from the admin bar above to view and take assessments.
          </AlertDescription>
        </Alert>
        
        <Card className="p-12 text-center">
          <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No Organization Selected</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            Use the organization selector in the admin bar to choose an organization and manage its safety assessments.
          </p>
        </Card>
      </div>
    );
  }

  // Organization context banner for super admins
  const OrgContextBanner = () => {
    if (!isSuperAdmin() || !isOrganizationMode || !selectedOrg) return null;
    return (
      <Alert className="bg-blue-500/10 border-blue-500/30 mb-4">
        <Building2 className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-600">
          Managing assessments for <strong>{selectedOrg.name}</strong>
        </AlertDescription>
      </Alert>
    );
  };

  // Start assessment: fetch questions for the selected org type
  const startAssessment = async () => {
    const selectedOrg = organizations.find(o => o.id === selectedOrgId);
    const orgType = selectedOrg?.org_type || 'sports';
    setSelectedOrgType(orgType);
    
    try {
      const questionsRes = await getAssessmentQuestions(orgType);
      setQuestions(questionsRes.data);
      setShowAssessment(true);
      setCurrentQuestion(0);
      setAnswers({});
    } catch (error) {
      toast.error('Failed to load assessment questions');
    }
  };

  // Update org type when selected org changes (only for super admin dropdown)
  const handleOrgChange = (orgId) => {
    setSelectedOrgId(orgId);
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      setOrgName(org.name);
      setSelectedOrgType(org.org_type || 'sports');
    }
    // Re-check eligibility for the new org
    checkEligibility(orgId);
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error('Please answer all questions');
      return;
    }

    // Validate organization is selected and matches context
    if (!selectedOrgId) {
      toast.error('Please select an organization');
      return;
    }

    // Double-check the org matches the context (for org admins)
    if (isOrganizationMode && selectedOrg && selectedOrgId !== selectedOrg.id) {
      toast.error(`Assessment must be submitted for ${selectedOrg.name}`);
      setSelectedOrgId(selectedOrg.id);
      return;
    }

    setSubmitting(true);
    try {
      const answersList = Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer: typeof answer === 'string' ? answer : parseInt(answer),
      }));

      const response = await createAssessment({
        organization_id: selectedOrgId,
        answers: answersList,
      });

      setResult(response.data);
      toast.success('Assessment completed!');
      // Refresh eligibility after submission
      checkEligibility(selectedOrgId);
    } catch (error) {
      // Handle specific error for 12-month restriction
      if (error.response?.data?.detail?.error === 'Assessment not yet eligible') {
        const detail = error.response.data.detail;
        const nextDate = new Date(detail.next_eligible_date).toLocaleDateString();
        toast.error(`Assessment not available until ${nextDate}. ${detail.message}`);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to submit assessment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetAssessment = () => {
    setShowAssessment(false);
    setCurrentQuestion(0);
    setAnswers({});
    setResult(null);
    // If we were viewing a specific assessment, navigate back to the list
    if (assessmentId) {
      navigate('/assessments');
    } else {
      fetchData();
    }
  };

  const getRiskColor = (level) => {
    const colors = {
      critical: 'text-[#FF3B30]',
      high: 'text-[#FF9500]',
      medium: 'text-[#FFCC00]',
      low: 'text-[#34C759]',
    };
    return colors[level] || 'text-primary';
  };

  const getRiskBgColor = (level) => {
    const colors = {
      critical: 'bg-[#FF3B30]/10 border-[#FF3B30]/30',
      high: 'bg-[#FF9500]/10 border-[#FF9500]/30',
      medium: 'bg-[#FFCC00]/10 border-[#FFCC00]/30',
      low: 'bg-[#34C759]/10 border-[#34C759]/30',
    };
    return colors[level] || 'bg-primary/10 border-primary/30';
  };

  // Get policies that are needed based on "No" answers - now from PH Library
  const getMissingPolicies = () => {
    if (!result?.answers || libraryPolicies.length === 0) return [];
    
    // Get question IDs that were answered "No"
    const noAnswerQuestions = result.answers
      .filter(a => a.answer === 0)
      .map(a => a.question_id);
    
    // Find relevant policies from library based on exact title matching
    const recommendedPolicies = [];
    const addedPolicyIds = new Set();
    
    noAnswerQuestions.forEach(questionId => {
      const policyTitles = QUESTION_POLICY_MAPPING[questionId] || [];
      
      // Skip if no policies mapped to this question
      if (policyTitles.length === 0) return;
      
      // Find library policies that match the mapped titles
      libraryPolicies.forEach(policy => {
        if (addedPolicyIds.has(policy.id)) return;
        
        const policyTitle = (policy.title || '').toLowerCase();
        
        // Check if this policy's title matches any of the mapped policy titles
        const matches = policyTitles.some(mappedTitle => 
          policyTitle.includes(mappedTitle.toLowerCase()) ||
          mappedTitle.toLowerCase().includes(policyTitle)
        );
        
        if (matches) {
          addedPolicyIds.add(policy.id);
          recommendedPolicies.push({
            ...policy,
            questionId,
            fromLibrary: true
          });
        }
      });
    });
    
    return recommendedPolicies;
  };

  // Generate and download policy document as branded PDF
  const downloadPolicy = async (policy) => {
    const policyKey = policy.id || policy.questionId;
    setDownloadingPolicy(policyKey);
    
    try {
      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      const organizationName = orgName || 'Your Organization';
      
      // Parse policy content - handle library policies with content field
      const rawContent = policy.content || '';
      const content = rawContent
        .replace(/\[ORGANIZATION_NAME\]/g, organizationName)
        .replace(/\[DATE\]/g, currentDate);

      // Create PDF document
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;

      // Helper function to add text with word wrap
      const addWrappedText = (text, x, y, maxWidth, fontSize, fontStyle = 'normal', color = [0, 0, 0]) => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontStyle);
        doc.setTextColor(color[0], color[1], color[2]);
        const lines = doc.splitTextToSize(text, maxWidth);
        doc.text(lines, x, y);
        return y + (lines.length * fontSize * 0.4);
      };

      // Helper to check if we need a new page
      const checkNewPage = (neededHeight) => {
        if (yPos + neededHeight > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Add logo or organization name header
      if (orgLogo) {
        try {
          doc.addImage(orgLogo, 'PNG', margin, yPos, 40, 15);
        } catch (e) {
          // Fallback to text if image fails
          doc.setFontSize(18);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 102, 204);
          doc.text(organizationName, margin, yPos + 10);
        }
      } else {
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 102, 204);
        doc.text(organizationName, margin, yPos + 10);
      }

      // Add date on the right
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(currentDate, pageWidth - margin, yPos + 10, { align: 'right' });

      yPos += 20;

      // Draw header line
      doc.setDrawColor(0, 102, 204);
      doc.setLineWidth(1);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 15;

      // Title
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      const titleLines = doc.splitTextToSize(policy.title.toUpperCase(), contentWidth);
      doc.text(titleLines, pageWidth / 2, yPos, { align: 'center' });
      yPos += titleLines.length * 8 + 5;

      // Blue accent line under title
      doc.setDrawColor(0, 102, 204);
      doc.setLineWidth(1.5);
      doc.line(pageWidth / 2 - 15, yPos, pageWidth / 2 + 15, yPos);
      yPos += 15;

      // Organization name and effective date
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(50, 50, 50);
      doc.text(organizationName, pageWidth / 2, yPos, { align: 'center' });
      yPos += 7;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Effective Date: ${currentDate}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Process content line by line
      const lines = content.split('\n');
      for (const line of lines) {
        const trimmedLine = line.trim();
        
        // Skip empty lines and already displayed info
        if (!trimmedLine || 
            trimmedLine.includes(policy.title.toUpperCase()) || 
            trimmedLine === organizationName ||
            trimmedLine.startsWith('Effective Date:')) {
          continue;
        }

        checkNewPage(15);

        // Section headers (1. PURPOSE, 2. DEFINITION, etc.)
        if (/^\d+\.\s+[A-Z]/.test(trimmedLine)) {
          yPos += 5;
          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 102, 204);
          
          // Draw left accent bar
          doc.setFillColor(0, 102, 204);
          doc.rect(margin, yPos - 4, 2, 6, 'F');
          
          const headerLines = doc.splitTextToSize(trimmedLine, contentWidth - 10);
          doc.text(headerLines, margin + 6, yPos);
          yPos += headerLines.length * 5 + 8;
        }
        // Sub-section headers (2.1, 5.1, etc.)
        else if (/^\d+\.\d+\s+/.test(trimmedLine)) {
          yPos += 3;
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(50, 50, 50);
          const subHeaderLines = doc.splitTextToSize(trimmedLine, contentWidth);
          doc.text(subHeaderLines, margin, yPos);
          yPos += subHeaderLines.length * 4.5 + 5;
        }
        // Bullet points
        else if (trimmedLine.startsWith('-')) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(60, 60, 60);
          
          // Draw bullet
          doc.setFillColor(0, 102, 204);
          doc.circle(margin + 3, yPos - 1.5, 1, 'F');
          
          const bulletText = trimmedLine.substring(1).trim();
          const bulletLines = doc.splitTextToSize(bulletText, contentWidth - 15);
          doc.text(bulletLines, margin + 8, yPos);
          yPos += bulletLines.length * 4 + 3;
        }
        // Step items
        else if (/^Step\s+\d+:/i.test(trimmedLine)) {
          yPos += 2;
          doc.setFillColor(248, 249, 250);
          doc.roundedRect(margin, yPos - 4, contentWidth, 10, 2, 2, 'F');
          
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(50, 50, 50);
          doc.text(trimmedLine, margin + 3, yPos + 2);
          yPos += 12;
        }
        // Signature lines
        else if (trimmedLine.includes('Signature:') || trimmedLine.includes('Acknowledged by:')) {
          yPos += 10;
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.3);
          doc.line(margin, yPos, pageWidth - margin, yPos);
          yPos += 8;
          
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(80, 80, 80);
          doc.text(trimmedLine, margin, yPos);
          yPos += 6;
        }
        // Regular text
        else {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(60, 60, 60);
          const textLines = doc.splitTextToSize(trimmedLine, contentWidth);
          doc.text(textLines, margin, yPos);
          yPos += textLines.length * 4 + 3;
        }
      }

      // Footer
      checkNewPage(40);
      yPos = Math.max(yPos + 15, pageHeight - 50);
      
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text(`This document is the property of ${organizationName}`, margin, yPos);
      yPos += 4;
      doc.text('Generated via Players Health Risk Management Platform', margin, yPos);

      // Disclaimer box
      yPos += 10;
      doc.setFillColor(255, 248, 230);
      doc.setDrawColor(255, 214, 102);
      doc.roundedRect(margin, yPos, contentWidth, 20, 2, 2, 'FD');
      
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(140, 109, 31);
      doc.text('Disclaimer:', margin + 3, yPos + 5);
      
      doc.setFont('helvetica', 'normal');
      const disclaimerText = 'Players Health provides this policy template as an educational resource only. Players Health is not responsible for the content, implementation, or legal sufficiency of any policy adopted by your organization. This template should be reviewed and customized by your organization\'s legal counsel before implementation.';
      const disclaimerLines = doc.splitTextToSize(disclaimerText, contentWidth - 6);
      doc.text(disclaimerLines, margin + 3, yPos + 9);

      // Save the PDF
      const filename = `${policy.filename}-${organizationName.toLowerCase().replace(/\s+/g, '-')}.pdf`;
      doc.save(filename);
      
      toast.success('Policy PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPolicy(null);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setOrgLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  // Show result with policy recommendations
  if (result) {
    const missingPolicies = getMissingPolicies();
    
    return (
      <div data-testid="assessment-result" className="max-w-3xl mx-auto space-y-6 animate-fade-in">
        {/* Score Card */}
        <Card className={`bg-card border ${getRiskBgColor(result.risk_level)}`}>
          <CardContent className="p-8 text-center">
            <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center ${getRiskBgColor(result.risk_level)} mb-6`}>
              <Shield className={`w-12 h-12 ${getRiskColor(result.risk_level)}`} />
            </div>
            <h2 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
              Safety Score
            </h2>
            <p className={`text-6xl font-bold font-['Barlow_Condensed'] mt-4 ${getRiskColor(result.risk_level)}`}>
              {result.score}%
            </p>
            <Badge className={`mt-4 uppercase ${getRiskBgColor(result.risk_level)} ${getRiskColor(result.risk_level)}`}>
              {result.risk_level} Risk
            </Badge>
          </CardContent>
        </Card>

        {/* Recommendations */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {result.recommendations && result.recommendations.length > 0 ? (
              <ul className="space-y-3">
                {result.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0 mt-2" aria-hidden="true" />
                    <span className="text-muted-foreground">{rec}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground">No specific recommendations - your organization is meeting safety standards.</p>
            )}
          </CardContent>
        </Card>

        {/* Missing Policies - Downloadable Templates */}
        {missingPolicies.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Policy Templates Available
              </CardTitle>
              <CardDescription>
                Based on your assessment, you're missing the following policies. Download and customize these templates for your organization.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Organization Details for Policy Customization */}
              <div className="p-4 rounded-md bg-secondary/30 space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Customize Your Policies
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Organization Name</Label>
                    <Input
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      placeholder="Enter your organization name"
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Organization Logo (Optional)</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="bg-secondary/50"
                    />
                  </div>
                </div>
                {orgLogo && (
                  <div className="flex items-center gap-2">
                    <img src={orgLogo} alt="Logo preview" className="h-10 w-auto" />
                    <span className="text-sm text-muted-foreground">Logo uploaded</span>
                  </div>
                )}
              </div>

              {/* Policy List */}
              <div className="space-y-3">
                {loadingPolicies ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
                    <span className="text-muted-foreground">Loading recommended policies from PH Library...</span>
                  </div>
                ) : missingPolicies.length > 0 ? (
                  missingPolicies.map((policy) => (
                    <div
                      key={policy.id || policy.questionId}
                      className="flex items-center justify-between p-4 rounded-md border border-border hover:bg-secondary/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{policy.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {policy.category && (
                              <Badge variant="outline" className="text-xs">{policy.category}</Badge>
                            )}
                            {policy.fromLibrary && (
                              <Badge className="bg-blue-500/20 text-blue-500 border-blue-500/30 text-xs">
                                PH Library
                              </Badge>
                            )}
                            {isPolicyAdopted(policy.id) && (
                              <Badge className="bg-green-500/20 text-green-500 border-green-500/30 text-xs">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Adopted
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedPolicy(policy);
                            setShowPolicyDialog(true);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Preview
                        </Button>
                        {isPolicyAdopted(policy.id) ? (
                          <Button
                            size="sm"
                            className="btn-primary-glow"
                            onClick={() => downloadPolicy(policy)}
                            disabled={downloadingPolicy === (policy.id || policy.questionId)}
                          >
                            {downloadingPolicy === (policy.id || policy.questionId) ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-1" />
                                Download PDF
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleAdoptPolicy(policy)}
                            disabled={adoptingPolicy === policy.id}
                          >
                            {adoptingPolicy === policy.id ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                Adopting...
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-1" />
                                Adopt Policy
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center p-8 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No matching policy templates found in the PH Library.</p>
                    <p className="text-sm mt-1">Visit Policy Management to add templates.</p>
                  </div>
                )}
              </div>

              {/* Legal Disclaimer */}
              <Alert className="bg-amber-500/10 border-amber-500/30">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <AlertDescription className="text-amber-700 dark:text-amber-300">
                  <strong>Important Legal Disclaimer:</strong> Players Health provides these policy templates as educational resources only. 
                  Players Health is not responsible for the content, implementation, or legal sufficiency of any policy adopted by your organization. 
                  These templates should be reviewed and customized by your organization's legal counsel before implementation. 
                  Your organization assumes full responsibility for any policies it adopts.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex gap-4">
          <Button onClick={resetAssessment} variant="outline" className="flex-1">
            Back to Assessments
          </Button>
          <Link to="/policies" className="flex-1">
            <Button className="w-full btn-primary-glow">
              View Safety Policies
            </Button>
          </Link>
        </div>

        {/* Policy Preview Dialog */}
        <Dialog open={showPolicyDialog} onOpenChange={setShowPolicyDialog}>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedPolicy?.title}</DialogTitle>
              <DialogDescription>
                Preview of the policy template. Download as a branded PDF for your organization.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <pre className="whitespace-pre-wrap text-sm p-4 rounded-md bg-secondary/30 font-mono max-h-[400px] overflow-y-auto">
                {selectedPolicy?.content
                  .replace(/\[ORGANIZATION_NAME\]/g, orgName || '[YOUR ORGANIZATION NAME]')
                  .replace(/\[DATE\]/g, new Date().toLocaleDateString())}
              </pre>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowPolicyDialog(false)}>
                Close
              </Button>
              <Button 
                className="btn-primary-glow"
                disabled={downloadingPolicy === selectedPolicy?.questionId}
                onClick={() => {
                  downloadPolicy(selectedPolicy);
                }}
              >
                {downloadingPolicy === selectedPolicy?.questionId ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Show assessment wizard
  if (showAssessment) {
    const currentQ = questions[currentQuestion];
    const progress = ((currentQuestion + 1) / questions.length) * 100;
    const isAnswered = answers[currentQ?.id] !== undefined;
    const isLastQuestion = currentQuestion === questions.length - 1;
    const allAnswered = Object.keys(answers).length === questions.length;

    // Render different question types
    const renderQuestionInput = () => {
      if (currentQ?.type === 'multiple_choice' && currentQ?.options) {
        // Multiple choice question (like the referral question)
        return (
          <RadioGroup
            key={currentQ?.id}
            value={answers[currentQ?.id]?.toString()}
            onValueChange={(value) => handleAnswer(currentQ?.id, value)}
            className="space-y-3"
          >
            {currentQ.options.map((option) => (
              <div key={option.value} className="flex items-center space-x-3 p-3 rounded-md hover:bg-secondary/30 transition-colors">
                <RadioGroupItem value={option.value} id={`opt-${option.value}`} />
                <Label htmlFor={`opt-${option.value}`} className="flex-1 cursor-pointer">
                  <span className="font-semibold mr-2">{option.value}.</span>
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );
      } else {
        // Yes/No question (default)
        return (
          <RadioGroup
            key={currentQ?.id}
            value={answers[currentQ?.id]?.toString()}
            onValueChange={(value) => handleAnswer(currentQ?.id, parseInt(value))}
            className="space-y-3"
          >
            <div className="flex items-center space-x-3 p-4 rounded-md border border-border hover:bg-secondary/30 transition-colors cursor-pointer">
              <RadioGroupItem value="1" id={`${currentQ?.id}-yes`} />
              <Label htmlFor={`${currentQ?.id}-yes`} className="flex-1 cursor-pointer flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-500" />
                </div>
                <span className="font-medium">Yes</span>
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-4 rounded-md border border-border hover:bg-secondary/30 transition-colors cursor-pointer">
              <RadioGroupItem value="0" id={`${currentQ?.id}-no`} />
              <Label htmlFor={`${currentQ?.id}-no`} className="flex-1 cursor-pointer flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center">
                  <X className="w-4 h-4 text-red-500" />
                </div>
                <span className="font-medium">No</span>
              </Label>
            </div>
          </RadioGroup>
        );
      }
    };

    return (
      <div data-testid="assessment-wizard" className="max-w-2xl mx-auto space-y-6 animate-fade-in">
        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Question {currentQuestion + 1} of {questions.length}</span>
            <span className="text-muted-foreground">{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question card */}
        <Card className="bg-card border-border">
          <CardHeader>
            <Badge variant="outline" className="w-fit mb-2">{currentQ?.category}</Badge>
            <CardTitle className="text-xl">
              {currentQ?.question}
            </CardTitle>
            {currentQ?.description && (
              <CardDescription className="text-sm mt-2">
                {currentQ.description}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {renderQuestionInput()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentQuestion === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {isLastQuestion ? (
            <Button
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              className="btn-primary-glow"
              data-testid="submit-assessment-btn"
            >
              {submitting ? 'Submitting...' : 'Complete Assessment'}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!isAnswered}
              data-testid="next-question-btn"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Show assessments list
  return (
    <div data-testid="assessments-page" className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Org Context Banner */}
      <OrgContextBanner />
      

      <div className="flex flex-col gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
            {selectedOrg ? `${selectedOrg.name} - Safety Assessment` : 'Safety Assessment'}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Evaluate your organization's safety protocols
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
          {/* Only show org selector for super admin or when not in org mode */}
          {(!isOrganizationMode || !selectedOrg) && organizations.length > 0 && (
            <Select value={selectedOrgId} onValueChange={handleOrgChange}>
              <SelectTrigger className="w-[160px] sm:w-[200px] text-xs sm:text-sm h-9 sm:h-10">
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>
                    <div className="flex items-center gap-2">
                      <span className="truncate">{org.name}</span>
                      <span className="text-xs text-muted-foreground hidden sm:inline">
                        ({org.org_type === 'fitness' ? 'Fitness' : 'Sports'})
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            onClick={startAssessment}
            className="btn-primary-glow text-xs sm:text-sm h-9 sm:h-10"
            data-testid="start-assessment-btn"
            disabled={checkingEligibility || (eligibility && !eligibility.eligible)}
          >
            {checkingEligibility ? (
              <>
                <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 animate-spin" />
                <span className="hidden sm:inline">Checking...</span>
                <span className="sm:hidden">...</span>
              </>
            ) : eligibility?.can_renew && !eligibility?.is_expired ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                <span className="hidden sm:inline">Renew Assessment</span>
                <span className="sm:hidden">Renew</span>
              </>
            ) : (
              <>
                <ClipboardCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                <span className="hidden sm:inline">Take {selectedOrgType === 'fitness' ? 'Fitness' : 'Sports'} Assessment</span>
                <span className="sm:hidden">Start</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Eligibility/Renewal Status Card */}
      {eligibility && !eligibility.eligible && (
        <Alert className="bg-amber-500/10 border-amber-500/30">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <AlertDescription className="text-amber-700">
            <strong>Assessment Not Available</strong>
            <p className="mt-1 text-sm">{eligibility.reason}</p>
            <div className="mt-2 flex flex-wrap gap-4 text-xs">
              {eligibility.last_assessment_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Last Assessment: {new Date(eligibility.last_assessment_date).toLocaleDateString()}
                </span>
              )}
              {eligibility.next_eligible_date && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Available: {new Date(eligibility.next_eligible_date).toLocaleDateString()} ({eligibility.days_until_eligible} days)
                </span>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Assessment Renewal Timeline (if there's an active assessment) */}
      {eligibility?.last_assessment_id && (
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Assessment Timeline
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-lg bg-secondary/30">
                <p className="text-[10px] sm:text-xs text-muted-foreground mb-1">Effective</p>
                <p className="font-semibold text-xs sm:text-sm">
                  {eligibility.effective_date ? new Date(eligibility.effective_date).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div className="p-2 sm:p-3 rounded-lg bg-secondary/30">
                <p className="text-[10px] sm:text-xs text-muted-foreground mb-1">Expiration</p>
                <p className="font-semibold text-xs sm:text-sm">
                  {eligibility.expiration_date ? new Date(eligibility.expiration_date).toLocaleDateString() : 'N/A'}
                </p>
                {eligibility.days_until_expiration !== null && (
                  <p className={`text-[10px] sm:text-xs mt-1 ${eligibility.days_until_expiration <= 60 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                    {eligibility.is_expired ? 'Expired' : `${eligibility.days_until_expiration}d left`}
                  </p>
                )}
              </div>
              <div className="p-2 sm:p-3 rounded-lg bg-secondary/30">
                <p className="text-[10px] sm:text-xs text-muted-foreground mb-1">Renewal Window</p>
                <p className="font-semibold text-xs sm:text-sm">
                  {eligibility.renewal_window_opens ? new Date(eligibility.renewal_window_opens).toLocaleDateString() : 'N/A'}
                </p>
                {eligibility.renewal_window_open && (
                  <Badge className="mt-1 bg-green-500/20 text-green-600 text-[10px] sm:text-xs">Open</Badge>
                )}
              </div>
              <div className="p-2 sm:p-3 rounded-lg bg-secondary/30">
                <p className="text-[10px] sm:text-xs text-muted-foreground mb-1">GL Renewal</p>
                <p className="font-semibold text-xs sm:text-sm">
                  {eligibility.gl_policy_renewal_date ? new Date(eligibility.gl_policy_renewal_date).toLocaleDateString() : 'Not Set'}
                </p>
                {!eligibility.gl_policy_renewal_date && (
                  <p className="text-[10px] text-muted-foreground mt-1 hidden sm:block">Optional</p>
                )}
              </div>
              <div className="p-2 sm:p-3 rounded-lg bg-secondary/30">
                <p className="text-[10px] sm:text-xs text-muted-foreground mb-1">Last Score</p>
                <div className="flex items-center gap-1 sm:gap-2">
                  <p className="font-semibold text-xs sm:text-sm">{eligibility.last_assessment_score}%</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-6 flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold mb-1">About Safety Assessments</h3>
            <p className="text-sm text-muted-foreground">
              {selectedOrgType === 'fitness' 
                ? 'Our fitness safety assessment evaluates your facility across key areas including staff certifications, equipment maintenance, member safety, emergency response, and professional conduct. Complete the assessment to receive a risk score, personalized recommendations, and downloadable policy templates.'
                : 'Our safety assessment evaluates your organization across key areas including risk management policies, background checks, training requirements, health and safety protocols, and compliance. Complete the assessment to receive a risk score, personalized recommendations, and downloadable policy templates.'
              }
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              <strong>Note:</strong> Assessments are valid for 12 months. Renewal window opens 60 days before expiration.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Past assessments */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
            Assessment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {assessments.length > 0 ? (
            <div className="space-y-3">
              {assessments.map((assessment) => (
                <div
                  key={assessment.id}
                  className="flex items-center justify-between p-4 rounded-md bg-secondary/30 hover:bg-secondary/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getRiskBgColor(assessment.risk_level)}`}>
                      <span className={`font-bold font-['Barlow_Condensed'] ${getRiskColor(assessment.risk_level)}`}>
                        {assessment.score}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">
                        {assessment.organization_name || orgName || 'Assessment'}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>
                          {new Date(assessment.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                        {assessment.is_expired && (
                          <Badge variant="outline" className="text-red-600 border-red-600/30 text-xs">Expired</Badge>
                        )}
                        {!assessment.is_expired && assessment.days_until_expiration <= 60 && (
                          <Badge variant="outline" className="text-amber-600 border-amber-600/30 text-xs">
                            Expires in {assessment.days_until_expiration} days
                          </Badge>
                        )}
                      </div>
                      <Badge className={`mt-1 ${getRiskBgColor(assessment.risk_level)} ${getRiskColor(assessment.risk_level)} uppercase text-xs`}>
                        {assessment.risk_level} Risk
                      </Badge>
                    </div>
                  </div>
                  <Link to={`/assessments/${assessment.id}`}>
                    <Button variant="ghost" size="sm">
                      View Details
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No assessments yet</p>
              <p className="text-sm mt-1">Take your first safety assessment to get started</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
